import { useCallback, useContext, useMemo, useState } from "react";
import { Colors } from "../../utils/colors";
import { Icon } from "@iconify/react";
import MainActionsDropdownMenu from "../DropdownsAndTabs/MainActionsDropdownMenu";
import MarkitHeaderLogo from "../MarkitHeaderLogo";
import TourwaveLogo from "../../../src/assets/TourwaveLogo.png";
import RectangleButton from "../Buttons/RectangleButton";
import HelpModal from "../Containers/HelpModal";
import { DarkTheme, useTheme } from "../../hooks/useTheme";
import { ThemeType } from "@markit/common.types";
import MarkitFullLogoWhite from "../../../src/assets/MarkitFullLogoWhite.png";
import { useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import { useNavigate } from "react-router-dom";
import { MixpanelContext } from "../../context/AnalyticsService";
import { MARKIT_DOCS } from "@markit/common.utils";
import { LandingPageSourceLabels } from "../../navigation/AppParamList";
import ProfileHeaderDropdown from "./CreatorProfile/ProfileHeaderDropdown";
import ProfilePic from "../ProfilePic";

type CreatorModeTopHeaderProps = { eventTheme?: ThemeType };

const CreatorModeTopHeader = (props: CreatorModeTopHeaderProps) => {
  const { eventTheme } = props;
  const accountData = useSelector(getAccountState).account.accountData;
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);
  const [helpModal, setHelpModal] = useState(false);
  const { theme } = useTheme(eventTheme);
  const navigate = useNavigate();
  const mixpanel = useContext(MixpanelContext);
  const isDarkTheme = useMemo(() => theme && theme === DarkTheme, [theme]);

  const styles = {
    headerButton: {
      height: 34,
      width: 34,
      borderRadius: 34 / 2,
      backgroundColor: Colors.GRAY10,
      border: `1px solid ${Colors.BLACK1}`,
      cursor: "pointer",
    },
  };

  const getMarkitDocsMixpanel = useCallback(
    (page: string) => {
      if (mixpanel) {
        mixpanel.track("Website: Navigate To Markit Docs", {
          distinct_id: accountData.uid,
          source: LandingPageSourceLabels[location.pathname],
          docs_page: page,
        });
      }
    },
    [accountData.uid, mixpanel]
  );

  return (
    <>
      <div
        className="AlignedRowSpaced"
        style={{
          minHeight: 55,
          height: 55,
          paddingInline: 14,
          backgroundColor: Colors.BLACK,
          borderBottom: isDarkTheme
            ? `0.5px solid ${Colors.BLACK1}`
            : undefined,
        }}
      >
        <div style={{ paddingInline: 8 }}>
          {accountData.isTourwaveUser ? (
            <div
              className="AlignedRowSelect"
              style={{ gap: 14 }}
              onClick={() => navigate("/home")}
            >
              <img
                style={{ height: 18, width: "auto" }}
                alt="TourwaveLogo"
                src={TourwaveLogo}
              />
              <span style={{ fontSize: 10, color: Colors.GRAY1 }}>
                powered by
              </span>
              <img
                style={{ height: 18, width: "auto" }}
                alt="MarkitFullIconTourwave"
                src={MarkitFullLogoWhite}
              />
            </div>
          ) : (
            <MarkitHeaderLogo alternateLogo={MarkitFullLogoWhite} />
          )}
        </div>
        <div className="AlignedRow" style={{ gap: 10 }}>
          <MainActionsDropdownMenu
            sourceLocation="creator_top_header"
            overlayStyles={{ width: 220 }}
          >
            <div
              className="Centering"
              style={styles.headerButton}
              onClick={() => setDropdownVisible(!dropdownVisible)}
            >
              <Icon
                icon="ion:add"
                height={26}
                style={{ color: Colors.WHITE }}
              />
            </div>
          </MainActionsDropdownMenu>
          <RectangleButton
            buttonLabel="Support"
            onPress={() => setHelpModal(true)}
            altPaddingHorz={12}
            altPaddingVert={10}
            altBorderRadius={100}
            altColor={Colors.GRAY10}
            altTextColor={Colors.WHITE}
            containerStyles={{
              border: `1px solid ${Colors.BLACK1}`,
              height: 35,
            }}
          />
          <RectangleButton
            buttonLabel="Docs"
            iconRight={
              <Icon
                icon="tabler:arrow-up-right"
                height={14}
                style={{ color: Colors.WHITE }}
              />
            }
            onPress={() => {
              getMarkitDocsMixpanel("home");
              window.open(MARKIT_DOCS);
            }}
            altPaddingHorz={12}
            altPaddingVert={10}
            altBorderRadius={100}
            altColor={Colors.GRAY10}
            altTextColor={Colors.WHITE}
            containerStyles={{
              border: `1px solid ${Colors.BLACK1}`,
              height: 35,
            }}
          />
          {accountData.isAdmin ? (
            <div
              className="Centering"
              style={styles.headerButton}
              title="Admin dashboard"
              onClick={() => navigate("/admin/master-dashboard")}
            >
              <Icon
                icon="mdi:view-dashboard-outline"
                height={19}
                style={{ color: Colors.WHITE }}
              />
            </div>
          ) : null}
          <ProfileHeaderDropdown
            placement="bottom"
            eventTheme={eventTheme}
            nonCreatorPage
          >
            <ProfilePic
              user={accountData}
              forcePointer
              size={35}
              blueGradientBackground
            />
          </ProfileHeaderDropdown>
        </div>
      </div>
      {helpModal ? (
        <HelpModal showModal={helpModal} setShowModal={setHelpModal} />
      ) : null}
    </>
  );
};

export default CreatorModeTopHeader;
